import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { Search, PackageX, PackageMinus, Boxes, IndianRupee, SlidersHorizontal, History, X } from 'lucide-react';
import toast from 'react-hot-toast';

const STAT_CARD_STYLE = { background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 };

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div style={STAT_CARD_STYLE}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={22} color={color} />
    </div>
    <div>
      <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>{title}</div>
      <div style={{ color: '#0f172a', fontSize: 24, fontWeight: 700 }}>{value}</div>
    </div>
  </div>
);

const REASONS = [
  { value: 'restock', label: 'Restock' },
  { value: 'correction', label: 'Correction' },
  { value: 'damage', label: 'Damage / Loss' },
  { value: 'other', label: 'Other' },
];

export default function AdminInventory() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [dashStats, setDashStats] = useState({ lowStock: 0, outOfStock: 0 });
  const [adjustProduct, setAdjustProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const limit = 15;

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = { page, limit, search };
      if (status !== 'all') params.status = status;
      const res = await adminAPI.getInventory(params);
      setProducts(res.data.products || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInventory(); }, [search, status, page]);

  useEffect(() => {
    adminAPI.getDashboard().then(r => setDashStats({
      lowStock: r.data.stats?.lowStock || 0,
      outOfStock: r.data.stats?.outOfStock || 0,
    })).catch(() => {});
  }, [products]);

  const openHistory = async (product) => {
    setHistoryProduct(product);
    try {
      const res = await adminAPI.getStockHistory(product.id);
      setHistory(res.data.history || []);
    } catch {
      toast.error('Failed to load stock history');
    }
  };

  const inventoryValue = products.reduce((s, p) => s + p.stock * parseFloat(p.price || 0), 0);
  const pages = Math.ceil(total / limit);

  const statusPill = (p) => {
    if (p.stock === 0) return { bg: '#fee2e2', text: '#dc2626', label: 'Out of Stock' };
    if (p.stock <= p.low_stock_threshold) return { bg: '#fef3c7', text: '#d97706', label: 'Low Stock' };
    return { bg: '#dcfce7', text: '#16a34a', label: 'In Stock' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Inventory</h1>
        <p style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>Stock levels, low-stock alerts, and adjustment history</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <StatCard title="Total SKUs" value={total} icon={Boxes} color="#6c3fc5" />
        <StatCard title="Low Stock" value={dashStats.lowStock} icon={PackageMinus} color="#f59e0b" />
        <StatCard title="Out of Stock" value={dashStats.outOfStock} icon={PackageX} color="#ef4444" />
        <StatCard title="Inventory Value (page)" value={`₹${inventoryValue.toFixed(0)}`} icon={IndianRupee} color="#22c55e" />
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 200 }}>
          <Search size={18} color="#94a3b8" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or SKU..." style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1, color: '#0f172a' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ v: 'all', l: 'All' }, { v: 'low', l: 'Low Stock' }, { v: 'out', l: 'Out of Stock' }].map(({ v, l }) => (
            <button key={v} onClick={() => { setStatus(v); setPage(1); }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: status === v ? '#f43397' : '#f1f5f9', color: status === v ? 'white' : '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Product', 'Seller', 'Stock', 'Threshold', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No products found</td></tr>
                ) : products.map(p => {
                  const pill = statusPill(p);
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.sku}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{p.seller_name || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{p.stock}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{p.low_stock_threshold}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: pill.bg, color: pill.text, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{pill.label}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => setAdjustProduct(p)} title="Adjust stock" style={{ background: '#ede9fe', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#7c3aed' }}><SlidersHorizontal size={15} /></button>
                          <button onClick={() => openHistory(p)} title="Stock history" style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#64748b' }}><History size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6, justifyContent: 'center' }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map(pg => (
              <button key={pg} onClick={() => setPage(pg)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: page === pg ? '#f43397' : '#f1f5f9', color: page === pg ? 'white' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{pg}</button>
            ))}
          </div>
        )}
      </div>

      {adjustProduct && (
        <AdjustStockModal
          product={adjustProduct}
          onClose={() => setAdjustProduct(null)}
          onSaved={() => { setAdjustProduct(null); fetchInventory(); }}
        />
      )}

      {historyProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Stock History — {historyProduct.name}</h2>
              <button onClick={() => setHistoryProduct(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color="#64748b" /></button>
            </div>
            <div style={{ padding: 24 }}>
              {history.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>No adjustments recorded yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {history.map(h => (
                    <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', textTransform: 'capitalize' }}>{h.reason}{h.note ? ` — ${h.note}` : ''}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(h.created_at).toLocaleString()} {h.adjusted_by_name ? `by ${h.adjusted_by_name}` : '(system)'}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: h.change_amount >= 0 ? '#22c55e' : '#ef4444' }}>
                        {h.change_amount >= 0 ? '+' : ''}{h.change_amount} <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>→ {h.new_stock}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdjustStockModal({ product, onClose, onSaved }) {
  const [changeAmount, setChangeAmount] = useState('');
  const [reason, setReason] = useState('restock');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const delta = parseInt(changeAmount);
    if (!delta) return toast.error('Enter a non-zero amount');
    setSaving(true);
    try {
      const res = await adminAPI.adjustStock(product.id, { change_amount: delta, reason, note });
      toast.success('Stock adjusted');
      onSaved(res.data.product);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to adjust stock');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 440 }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Adjust Stock</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color="#64748b" /></button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, color: '#64748b' }}>{product.name} — current stock: <strong style={{ color: '#0f172a' }}>{product.stock}</strong></div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Change amount (use negative to remove)</label>
            <input type="number" value={changeAmount} onChange={e => setChangeAmount(e.target.value)} placeholder="e.g. 20 or -5" style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Reason</label>
            <select value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }}>
              {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Note (optional)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#f43397', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
